/**
 * wf-actions — the four personal-banker actions on My Day, built to the shared
 * reference screens (2026-07-27 operator video + captures):
 *   · BookAppointmentSheet — "Book new appointment"
 *   · TimeOffRequestModal  — "Request time off" (reason cards + PTO impact)
 *   · SwapRequestModal     — "Initiate shift swap" (shift picker + routing)
 * Report sick already matched its screen and stays in WorkforceMyDay.
 *
 * Every list is served, never authored here: services + slots come from the
 * appointments catalogue ops, advisors from the staff actually bookable for that
 * service/date, recent customers from the signed-in banker's own served day, and
 * shifts/PTO from the workforce ops. Nothing renders until its op answers; a
 * missing op degrades to an honest inline note rather than an invented list.
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import { Drawer, Modal } from "../../shell/Overlay";
import { listAppointmentServices, listApptSlots, type ApptChannel, type ApptServiceRef, type ApptSlot } from "../appointments/appointments-api";
import { getMyUpcomingShifts, getMyTime, type MyDayResponse, type TimeOffRequest } from "./wf-api";
import { dayLabel, hmLabel, hmToMin, isoToday, addDaysIso } from "./wf-shared";

const FieldLabel = ({ children, hint }: { children: React.ReactNode; hint?: string }) => (
  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1, marginBottom: 8 }}>
    {children}{hint ? <span style={{ fontWeight: 400, color: T.soft }}> {hint}</span> : null}
  </div>
);

function choiceStyle(active: boolean, tone: string): React.CSSProperties {
  return {
    padding: "11px 13px", borderRadius: 9, textAlign: "left", cursor: "pointer", fontFamily: T.font,
    border: `1.5px solid ${active ? tone : T.n5}`, background: active ? alpha(tone, 7) : T.white,
    color: active ? tone : T.n1, fontSize: 13, fontWeight: active ? 700 : 500, lineHeight: 1.35,
  };
}
const initialsOf = (name: string) => name.split(/[\s.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/* ── Book new appointment ───────────────────────────────────────────── */

const METHODS: { id: "in-person" | "phone" | "video"; label: string; labelKey: string; channel: ApptChannel; icon: "users" | "phone" | "video" }[] = [
  { id: "in-person", label: "In branch", labelKey: "workforce.book.inBranch", channel: "inPerson", icon: "users" },
  { id: "phone", label: "Phone", labelKey: "workforce.book.phone", channel: "phone", icon: "phone" },
  { id: "video", label: "Teams call", labelKey: "workforce.book.teamsCall", channel: "video", icon: "video" },
];

export interface BookApptSubmit { customer: string; service: string; date: string; time: string; durMin: number; method: string; note?: string }

/* ── Walk-in (customer is standing there) ────────────────────────────
 * Deliberately not the scheduling sheet: no date, no time, no method, no
 * advisor picker. They're in the branch, with this banker, now — so the only
 * two questions are who and what for. The time is stamped when it saves.
 */
export function WalkInSheet({ myDay, advisorName, busy, onClose, onSubmit }: {
  myDay: MyDayResponse | null; advisorName: string; busy: string | null;
  onClose: () => void; onSubmit: (input: BookApptSubmit) => void;
}) {
  const t = useT();
  const [services, setServices] = useState<ApptServiceRef[] | null>(null);
  const [svcError, setSvcError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState<string | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    listAppointmentServices()
      .then((r) => { if (live) setServices(r.services || []); })
      .catch((e) => { if (live) setSvcError(e instanceof Error && e.message ? e.message : t("workforce.book.serviceCatalogueUnavailable", "The service catalogue isn't available right now.")); });
    return () => { live = false; };
  }, [t]);

  const recents = useMemo(() => {
    const seen = new Map<string, { name: string; tier: string }>();
    for (const a of myDay?.appointments || []) {
      if (!seen.has(a.customer)) seen.set(a.customer, { name: a.customer, tier: a.customer360?.tier || "" });
    }
    return Array.from(seen.values());
  }, [myDay]);
  const q = query.trim();
  const matches = useMemo(() => {
    const needle = q.toLowerCase();
    return needle ? recents.filter((c) => c.name.toLowerCase().includes(needle)) : recents;
  }, [recents, q]);
  /* Someone who has never been served by this banker won't be on the list — the
     typed name is the customer, so the desk is never blocked by a missing record. */
  const newName = q && !matches.some((c) => c.name.toLowerCase() === q.toLowerCase()) ? q : null;
  const service = (services || []).find((s) => s.id === serviceId) || null;
  const ready = !!customer && !!service;

  return (
    <Drawer panel="walk-in" width={460} icon={<Icon.users size={16} />} title={t("workforce.walkIn.title", "Walk-in")}
      sub={t("workforce.walkIn.sub", "Books to you, in branch, starting when you save.")}
      onClose={onClose}
      footer={<>
        <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.n2, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{t("common.actions.cancel", "Cancel")}</button>
        <Btn testid="wfwalkin-submit" disabled={!ready || busy !== null}
          onClick={() => {
            const now = new Date();
            const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
            const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
            onSubmit({ customer: customer as string, service: (service as ApptServiceRef).name, date, time, durMin: service?.duration || 30, method: "in-person", note: note || undefined });
          }}>
          {busy === "book" ? t("workforce.walkIn.starting", "Starting…") : t("workforce.walkIn.startAppointment", "Start appointment")}
        </Btn>
      </>}>

      <div>
        <FieldLabel>{t("workforce.book.customer", "Customer")}</FieldLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 12px", border: `1px solid ${T.n5}`, borderRadius: 9, marginBottom: 10 }}>
          <span style={{ color: T.soft, display: "inline-flex" }}><Icon.search size={14} /></span>
          <input value={query} autoFocus data-testid="wfwalkin-customer-search"
            onChange={(e) => { setQuery(e.target.value); if (customer && customer !== e.target.value) setCustomer(null); }}
            placeholder={t("workforce.walkIn.searchOrTypeName", "Search or type the customer's name")}
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: T.font, fontSize: 13.5, color: T.n1 }} />
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", maxHeight: 210, overflowY: "auto" }}>
          {newName ? (
            <button onClick={() => setCustomer(newName)} data-testid="wfwalkin-new-customer"
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: "none", background: customer === newName ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
              <span style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center", background: customer === newName ? T.p1 : T.n6, color: customer === newName ? "#fff" : T.n2 }}><Icon.plus size={13} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{newName}</span>
                <span style={{ display: "block", fontSize: 11, color: T.soft }}>{t("workforce.walkIn.notOnYourList", "Not on your recent list — use this name")}</span>
              </span>
              {customer === newName ? <span style={{ color: T.p1, display: "inline-flex" }}><Icon.check size={14} /></span> : null}
            </button>
          ) : null}
          {matches.map((c) => {
            const on = customer === c.name;
            return (
              <button key={c.name} onClick={() => setCustomer(on ? null : c.name)} data-testid="wfwalkin-customer" data-id={c.name}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: "none", borderTop: `1px solid ${T.n6}`, background: on ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center", background: on ? T.p1 : T.n6, color: on ? "#fff" : T.n2, fontSize: 11, fontWeight: 700 }}>{initialsOf(c.name)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{c.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: "0.04em" }}>{c.tier}</span>
                </span>
                {on ? <span style={{ color: T.p1, display: "inline-flex" }}><Icon.check size={14} /></span> : null}
              </button>
            );
          })}
          {!matches.length && !newName ? (
            <div style={{ padding: "14px 12px", fontSize: 12.5, color: T.soft }}>{t("workforce.walkIn.startTypingAName", "Start typing a name.")}</div>
          ) : null}
        </div>
      </div>

      <div>
        <FieldLabel>{t("workforce.book.service", "Service")}</FieldLabel>
        {svcError ? (
          <div style={{ padding: "12px 14px", border: `1px dashed ${T.n4}`, borderRadius: 9, fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{svcError}</div>
        ) : !services ? (
          <div style={{ padding: "12px 14px", fontSize: 12.5, color: T.soft }}>{t("workforce.book.loadingServices", "Loading the service catalogue…")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
            {services.map((s) => {
              const on = serviceId === s.id;
              return (
                <button key={s.id} onClick={() => setServiceId(on ? null : s.id)} data-testid="wfwalkin-service" data-id={s.id}
                  style={{ ...choiceStyle(on, T.p1), display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                  <span style={{ minWidth: 0 }}>{s.name}</span>
                  <span style={{ fontSize: 11, color: on ? T.p1 : T.soft, fontFamily: T.fontMono, flexShrink: 0 }}>{s.duration}{t("workforce.book.minShort", "m")}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <FieldLabel hint={t("workforce.book.optional", "(optional)")}>{t("workforce.book.notes", "Notes")}</FieldLabel>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="wfwalkin-notes"
          placeholder={t("workforce.walkIn.notesPlaceholder", "Reason for the visit, anything to prepare…")}
          style={{ width: "100%", padding: 11, fontFamily: T.font, fontSize: 13, lineHeight: 1.5, border: `1px solid ${T.n5}`, borderRadius: 9, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
      </div>

      <div style={{ background: T.n6, borderRadius: 9, padding: "10px 13px", fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
        {t("workforce.walkIn.assignedTo", "Assigned to")}{" "}<b style={{ color: T.n1 }}>{advisorName}</b> · {t("workforce.walkIn.inBranchNow", "in branch, starting now")}{" "}{service ? <> · {service.duration}{t("workforce.book.minShort", "m")}</> : null}
      </div>
    </Drawer>
  );
}

/** `initialTime` (additive, 2026-08-01) lets a caller open the sheet on a slot the
 *  user already pointed at — the day board's free gaps and drag-selected ranges.
 *  It is a PREFILL, not a promise: once listApptSlots answers, a time the op
 *  doesn't actually offer is dropped rather than left sitting there bookable. */
export function BookAppointmentSheet({ myDay, date, advisorName, busy, initialTime, onClose, onSubmit }: {
  myDay: MyDayResponse | null; date: string; advisorName: string; busy: string | null; initialTime?: string;
  onClose: () => void; onSubmit: (input: BookApptSubmit) => void;
}) {
  const t = useT();
  const [services, setServices] = useState<ApptServiceRef[] | null>(null);
  const [svcError, setSvcError] = useState<string | null>(null);
  const [slots, setSlots] = useState<ApptSlot[] | null>(null);
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState<string | null>(null);
  const [advisor, setAdvisor] = useState(advisorName);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [day, setDay] = useState(date);
  const [pickDay, setPickDay] = useState(false);
  const [time, setTime] = useState<string | null>(initialTime ?? null);
  const [method, setMethod] = useState<"in-person" | "phone" | "video">("in-person");
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    listAppointmentServices()
      .then((r) => { if (live) setServices(r.services || []); })
      .catch((e) => { if (live) setSvcError(e instanceof Error && e.message ? e.message : t("workforce.book.serviceCatalogueUnavailable", "The service catalogue isn't available right now.")); });
    return () => { live = false; };
  }, [t]);

  const service = useMemo(() => (services || []).find((s) => s.id === serviceId) || null, [services, serviceId]);

  /* A prefilled time only survives if the slots op agrees it is open. */
  useEffect(() => { if (slots && time && !slots.some((s) => s.start === time)) setTime(null); }, [slots, time]);

  useEffect(() => {
    if (!service) { setSlots(null); return; }
    let live = true;
    setSlots(null);
    listApptSlots({ service: service.id, date: day })
      .then((r) => { if (live) setSlots(r.slots || []); })
      .catch(() => { if (live) setSlots([]); });
    return () => { live = false; };
  }, [service, day]);

  /* Recent customers — the banker's own served day, most recent first. */
  const recents = useMemo(() => {
    const seen = new Map<string, { name: string; tier: string; last: string }>();
    for (const a of myDay?.appointments || []) {
      if (!seen.has(a.customer)) seen.set(a.customer, { name: a.customer, tier: a.customer360?.tier || "", last: a.start });
    }
    return Array.from(seen.values());
  }, [myDay]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? recents.filter((c) => c.name.toLowerCase().includes(q)) : recents;
  }, [recents, query]);

  /* Advisors — whoever the slot grid says can take this service, plus you. */
  const advisors = useMemo(() => {
    const names = new Set<string>([advisorName]);
    for (const s of slots || []) if (s.staffName) names.add(s.staffName);
    return Array.from(names);
  }, [slots, advisorName]);

  /* The grid is the shift window PLUS every time the slots op offers, so a day
     outside today's shift pattern still shows its real openings; anything the op
     doesn't offer renders struck through. */
  const grid = useMemo(() => {
    const shift = myDay?.shift;
    const startMin = shift ? hmToMin(shift.start) : 9 * 60;
    const endMin = shift ? hmToMin(shift.end) : 17 * 60;
    const step = 30;
    const open = new Set((slots || []).map((s) => s.start));
    const times = new Set<string>(open);
    for (let m = startMin; m + (service?.duration || 30) <= endMin; m += step) {
      times.add(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
    return Array.from(times).sort().map((time) => ({ time, free: open.has(time) }));
  }, [myDay, slots, service]);

  const methodsForService = useMemo(() => {
    const ch = service?.channels;
    return !ch || !ch.length ? METHODS : METHODS.filter((m) => ch.includes(m.channel));
  }, [service]);
  useEffect(() => { if (methodsForService.length && !methodsForService.some((m) => m.id === method)) setMethod(methodsForService[0].id); }, [methodsForService, method]);

  const ready = !!customer && !!service && !!time;
  const dayOptions = useMemo(() => Array.from({ length: 21 }, (_, i) => addDaysIso(isoToday(), i)), []);

  return (
    <Drawer panel="book-appointment" width={560} icon={<Icon.plus size={16} />} title={t("workforce.book.title", "Book new appointment")} onClose={onClose}
      footer={<>
        <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.n2, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{t("common.actions.cancel", "Cancel")}</button>
        <Btn testid="wfbook-submit" disabled={!ready || busy !== null}
          onClick={() => onSubmit({ customer: customer as string, service: (service as ApptServiceRef).name, date: day, time: time as string, durMin: service?.duration || 30, method, note: note || undefined })}>
          {busy === "book" ? t("workforce.book.booking", "Booking…") : t("workforce.book.bookAppointment", "Book appointment")}
        </Btn>
      </>}>

      <div>
        <FieldLabel>{t("workforce.book.customer", "Customer")}</FieldLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 12px", border: `1px solid ${T.n5}`, borderRadius: 9, marginBottom: 12 }}>
          <span style={{ color: T.soft, display: "inline-flex" }}><Icon.search size={14} /></span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="wfbook-customer-search"
            placeholder={t("workforce.book.searchByNameEmailOrCustomerId", "Search by name, email, or customer ID")}
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: T.font, fontSize: 13.5, color: T.n1 }} />
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", background: T.n6, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: T.soft }}>
            {t("workforce.book.recentCustomers", "Recent customers")}
          </div>
          {filtered.length ? filtered.map((c) => {
            const on = customer === c.name;
            return (
              <button key={c.name} onClick={() => setCustomer(on ? null : c.name)} data-testid="wfbook-recent-customer" data-id={c.name}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: "none", borderTop: `1px solid ${T.n6}`, background: on ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center", background: on ? T.p1 : T.n6, color: on ? "#fff" : T.n2, fontSize: 11, fontWeight: 700 }}>{initialsOf(c.name)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{c.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: "0.04em" }}>{c.tier}{c.tier ? " · " : ""}{t("workforce.book.lastSeen", "last seen")}{" "}{hmLabel(c.last)}</span>
                </span>
                <span style={{ color: on ? T.p1 : T.n4, display: "inline-flex" }}>{on ? <Icon.check size={14} /> : <Icon.chev dir="right" size={13} />}</span>
              </button>
            );
          }) : (
            <div style={{ padding: "14px 12px", fontSize: 12.5, color: T.soft, borderTop: `1px solid ${T.n6}` }}>
              {query ? t("workforce.book.noMatchingCustomer", "No customer on your recent list matches that search.") : t("workforce.book.noRecentCustomers", "No recent customers on today's book yet.")}
            </div>
          )}
        </div>
      </div>

      <div>
        <FieldLabel hint={t("workforce.book.advisorHint", "(book for yourself or another staff member)")}>{t("workforce.book.advisor", "Advisor")}</FieldLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: `1px solid ${T.border}`, borderRadius: 10 }}>
          <span style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center", background: T.p4, color: T.p1, fontSize: 12, fontWeight: 700 }}>{initialsOf(advisor)}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>
              {advisor}
              {advisor === advisorName ? <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", color: T.p1, background: T.p4, borderRadius: 4, padding: "2px 5px" }}>{t("workforce.book.you", "YOU")}</span> : null}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: T.soft, marginTop: 1 }}>{myDay?.locationName || ""}</span>
          </span>
          <select value={advisor} onChange={(e) => setAdvisor(e.target.value)} data-testid="wfbook-advisor-select"
            style={{ maxWidth: 240, height: 34, padding: "0 8px", border: `1px solid ${T.n5}`, borderRadius: 8, background: T.white, fontFamily: T.font, fontSize: 12.5, color: T.n1, cursor: "pointer" }}>
            {advisors.map((a) => <option key={a} value={a}>{a === advisorName ? `${a} (${t("workforce.book.youLower", "you")})` : a}</option>)}
          </select>
        </div>
      </div>

      <div>
        <FieldLabel hint={service ? `(${service.duration}${t("workforce.book.minShort", "m")})` : undefined}>{t("workforce.book.service", "Service")}</FieldLabel>
        {svcError ? (
          <div style={{ padding: "12px 14px", border: `1px dashed ${T.n4}`, borderRadius: 9, fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{svcError}</div>
        ) : !services ? (
          <div style={{ padding: "12px 14px", fontSize: 12.5, color: T.soft }}>{t("workforce.book.loadingServices", "Loading the service catalogue…")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
            {services.map((s) => {
              const on = serviceId === s.id;
              return (
                <button key={s.id} onClick={() => { setServiceId(on ? null : s.id); setTime(null); }} data-testid="wfbook-service" data-id={s.id}
                  style={{ ...choiceStyle(on, T.p1), display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                  <span style={{ minWidth: 0 }}>{s.name}</span>
                  <span style={{ fontSize: 11, color: on ? T.p1 : T.soft, fontFamily: T.fontMono, flexShrink: 0 }}>{s.duration}{t("workforce.book.minShort", "m")}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <FieldLabel>{t("workforce.book.date", "Date")}</FieldLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", border: `1px solid ${T.n5}`, borderRadius: 9 }}>
          <span style={{ color: T.soft, display: "inline-flex" }}><Icon.calendar size={14} /></span>
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{dayLabel(day, "full")}</span>
          <button onClick={() => setPickDay((v) => !v)} data-testid="wfbook-change-date"
            style={{ border: "none", background: "transparent", color: T.p1, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: T.font }}>
            {pickDay ? t("common.actions.close", "Close") : t("workforce.book.change", "Change")}
          </button>
        </div>
        {pickDay ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 9 }}>
            {dayOptions.map((d) => (
              <button key={d} onClick={() => { setDay(d); setTime(null); setPickDay(false); }}
                style={{ ...choiceStyle(d === day, T.p1), padding: "7px 10px", fontSize: 12 }}>{dayLabel(d, "md")}</button>
            ))}
          </div>
        ) : null}
      </div>

      <div>
        <FieldLabel hint={service ? `(${service.duration}${t("workforce.book.minuteBlock", "-minute block")})` : undefined}>{t("workforce.book.timeSlot", "Time slot")}</FieldLabel>
        {!service ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.book.pickAServiceFirst", "Pick a service to see open times.")}</div>
        ) : slots === null ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.book.checkingAvailability", "Checking availability…")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
            {grid.map((g) => (
              <button key={g.time} disabled={!g.free} onClick={() => setTime(g.time)} data-testid="wfbook-slot" data-id={g.time}
                style={{ ...choiceStyle(time === g.time, T.p1), padding: "8px 4px", textAlign: "center", fontFamily: T.fontMono, fontSize: 12.5,
                  cursor: g.free ? "pointer" : "not-allowed", opacity: g.free ? 1 : 0.45, textDecoration: g.free ? "none" : "line-through", color: g.free ? (time === g.time ? T.p1 : T.n1) : T.n3 }}>
                {g.time}
              </button>
            ))}
            {!grid.length ? <span style={{ gridColumn: "1 / -1", fontSize: 12.5, color: T.soft }}>{t("workforce.book.noShiftThatDay", "You have no shift that day, so there are no times to offer.")}</span>
              : !grid.some((g) => g.free) ? <span style={{ gridColumn: "1 / -1", fontSize: 12.5, color: T.soft, marginTop: 4 }}>{t("workforce.book.noOpenTimes", "Nothing open on this day — try another date.")}</span> : null}
          </div>
        )}
      </div>

      <div>
        <FieldLabel>{t("workforce.book.method", "Method")}</FieldLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {methodsForService.map((m) => {
            const Ico = Icon[m.icon];
            return (
              <button key={m.id} onClick={() => setMethod(m.id)} data-testid="wfbook-method" data-id={m.id}
                style={{ ...choiceStyle(method === m.id, T.p1), display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px" }}>
                <Ico size={13} /> {t(m.labelKey, m.label)}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <FieldLabel hint={t("workforce.book.optional", "(optional)")}>{t("workforce.book.notes", "Notes")}</FieldLabel>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="wfbook-notes"
          placeholder={t("workforce.book.notesPlaceholder", "Talking points, documents to prepare, follow-up from last visit…")}
          style={{ width: "100%", padding: 11, fontFamily: T.font, fontSize: 13, lineHeight: 1.5, border: `1px solid ${T.n5}`, borderRadius: 9, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
      </div>
    </Drawer>
  );
}

/* ── Request time off ───────────────────────────────────────────────── */

const REASONS: { id: TimeOffRequest["type"]; label: string; labelKey: string; pto: boolean }[] = [
  { id: "vacation", label: "Vacation", labelKey: "workforce.timeOff.vacation", pto: true },
  { id: "personal", label: "Personal day", labelKey: "workforce.timeOff.personalDay", pto: true },
  { id: "sick", label: "Medical", labelKey: "workforce.timeOff.medical", pto: false },
  { id: "other", label: "Bereavement", labelKey: "workforce.timeOff.bereavement", pto: false },
];
const workingDaysBetween = (fromIso: string, toIso: string): number => {
  const from = new Date(fromIso + "T00:00:00"), to = new Date(toIso + "T00:00:00");
  if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) return 0;
  let n = 0;
  for (const d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) { const dow = d.getDay(); if (dow !== 0 && dow !== 6) n += 1; }
  return n;
};

export function TimeOffRequestModal({ busy, initialDate, ptoBalanceDays, onClose, onSubmit }: {
  busy: string | null; initialDate?: string; ptoBalanceDays?: number | null;
  onClose: () => void; onSubmit: (i: { type: TimeOffRequest["type"]; startDate: string; endDate: string; allDay: boolean; note?: string }) => void;
}) {
  const t = useT();
  const [from, setFrom] = useState(() => initialDate ?? addDaysIso(isoToday(), 1));
  const [to, setTo] = useState(() => initialDate ?? addDaysIso(isoToday(), 1));
  const [type, setType] = useState<TimeOffRequest["type"]>("vacation");
  const [note, setNote] = useState("");
  // The PTO figure in the impact line is the served balance, never a guess: the
  // caller can pass it, otherwise we read it from the same op My Time uses.
  const [pto, setPto] = useState<number | null>(typeof ptoBalanceDays === "number" ? ptoBalanceDays : null);
  useEffect(() => {
    if (typeof ptoBalanceDays === "number") { setPto(ptoBalanceDays); return; }
    let live = true;
    getMyTime().then((r) => { if (live && r && r.kpis) setPto(r.kpis.ptoBalanceDays); }).catch(() => { /* impact line degrades to working days only */ });
    return () => { live = false; };
  }, [ptoBalanceDays]);
  const valid = to >= from;
  const days = workingDaysBetween(from, to);
  const countsPto = REASONS.find((r) => r.id === type)?.pto ?? true;
  const dateInput: React.CSSProperties = { width: "100%", height: 40, padding: "0 11px", border: `1px solid ${T.n5}`, borderRadius: 9, fontFamily: T.font, fontSize: 13.5, color: T.n1, boxSizing: "border-box", background: T.white };

  return (
    <Modal icon={<Icon.mail size={16} />} title={t("workforce.timeOff.title", "Request time off")}
      sub={t("workforce.timeOff.sub", "Branch manager is notified immediately. Booked appointments will be flagged for reassignment.")}
      onClose={onClose}
      footer={<>
        <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.n2, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{t("common.actions.cancel", "Cancel")}</button>
        <Btn testid="wftimeoff-submit" disabled={!valid || busy !== null} onClick={() => onSubmit({ type, startDate: from, endDate: to, allDay: true, note: note || undefined })}>
          {busy === "timeoff" ? t("workforce.timeOff.submitting", "Submitting…") : t("workforce.timeOff.submitRequest", "Submit request")}
        </Btn>
      </>}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <FieldLabel>{t("workforce.timeOff.from", "From")}</FieldLabel>
          <input type="date" value={from} min={isoToday()} data-testid="wftimeoff-from"
            onChange={(e) => { const v = e.target.value; setFrom(v); if (to < v) setTo(v); }} style={dateInput} />
        </div>
        <div>
          <FieldLabel>{t("workforce.timeOff.to", "To")}</FieldLabel>
          <input type="date" value={to} min={from} data-testid="wftimeoff-to" onChange={(e) => setTo(e.target.value)} style={dateInput} />
        </div>
      </div>

      <div>
        <FieldLabel>{t("workforce.timeOff.reason", "Reason")}</FieldLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {REASONS.map((r) => (
            <button key={r.id} onClick={() => setType(r.id)} data-testid="wftimeoff-reason" data-id={r.id} style={choiceStyle(type === r.id, T.p1)}>
              <span style={{ display: "block" }}>{t(r.labelKey, r.label)}</span>
              <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: T.soft, marginTop: 2 }}>
                {r.pto ? t("workforce.timeOff.countsAgainstPto", "Counts against PTO") : t("workforce.timeOff.doesnTCountAgainstPto", "Doesn't count against PTO")}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <FieldLabel hint={t("workforce.timeOff.optional", "(optional)")}>{t("workforce.timeOff.notesForYourManager", "Notes for your manager")}</FieldLabel>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="wftimeoff-note"
          placeholder={t("workforce.timeOff.notePlaceholder", "Anything they should know — e.g. flights, coverage suggestions")}
          style={{ width: "100%", padding: 11, fontFamily: T.font, fontSize: 13, lineHeight: 1.5, border: `1px solid ${T.n5}`, borderRadius: 9, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
      </div>

      {!valid ? <div style={{ fontSize: 12.5, color: T.red1 }}>{t("workforce.timeOff.toBeforeFrom", "The last day can't be before the first day.")}</div> : (
        <div style={{ background: T.n6, borderRadius: 9, padding: "11px 14px" }} data-testid="wftimeoff-impact">
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: T.soft, marginBottom: 4 }}>{t("workforce.timeOff.impact", "Impact")}</div>
          <div style={{ fontSize: 12.5, color: T.n1 }}>
            {days} {days === 1 ? t("workforce.timeOff.workingDay", "working day") : t("workforce.timeOff.workingDays", "working days")}{" "}{countsPto && typeof pto === "number" ? (
              <> · <span style={{ color: T.n2 }}>{pto}</span> → <span style={{ fontWeight: 700, color: pto - days < 0 ? T.red1 : T.green1 }}>{Math.round((pto - days) * 10) / 10}</span> {t("workforce.timeOff.ptoDays", "PTO days")}</>
            ) : !countsPto ? <> · {t("workforce.timeOff.doesnTCountAgainstPto", "Doesn't count against PTO")}</> : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Initiate shift swap ────────────────────────────────────────────── */

export function SwapRequestModal({ busy, onClose, onSubmit }: {
  busy: string | null; onClose: () => void; onSubmit: (i: { shiftId: string; route: "marketplace" | "direct" }) => void;
}) {
  const t = useT();
  const [shifts, setShifts] = useState<{ id: string; date: string; start: string; end: string; apptCount: number }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [route, setRoute] = useState<"marketplace" | "direct">("marketplace");

  useEffect(() => {
    let live = true;
    getMyUpcomingShifts()
      .then((r) => { if (!live) return; const list = r.shifts || []; setShifts(list); setPicked(list.length ? list[0].id : null); })
      .catch((e) => { if (live) setError(e instanceof Error && e.message ? e.message : t("workforce.swap.shiftsUnavailable", "Your upcoming shifts aren't available right now.")); });
    return () => { live = false; };
  }, [t]);

  return (
    <Drawer panel="shift-swap" width={480} icon={<Icon.users size={16} />} title={t("workforce.swap.title", "Initiate shift swap")}
      sub={t("workforce.swap.sub", "Trade a shift with a colleague, or post it to the marketplace for anyone to pick up.")}
      onClose={onClose}
      footer={<>
        <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.n2, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{t("common.actions.cancel", "Cancel")}</button>
        <Btn testid="wfswap-submit" disabled={!picked || busy !== null} onClick={() => onSubmit({ shiftId: picked as string, route })}>
          {busy === "swap" ? t("workforce.swap.submitting", "Submitting…")
            : route === "marketplace" ? t("workforce.swap.postToMarketplace", "Post to marketplace") : t("workforce.swap.sendSwapRequest", "Send swap request")}
        </Btn>
      </>}>
      <div>
        <FieldLabel>{t("workforce.swap.whichShift", "Which shift do you want to swap?")}</FieldLabel>
        {error ? (
          <div style={{ padding: "12px 14px", border: `1px dashed ${T.n4}`, borderRadius: 9, fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{error}</div>
        ) : !shifts ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.swap.loadingShifts", "Loading your upcoming shifts…")}</div>
        ) : !shifts.length ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.swap.noUpcomingShifts", "You have no upcoming shifts to swap.")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {shifts.map((s) => {
              const on = picked === s.id;
              return (
                <button key={s.id} onClick={() => setPicked(s.id)} data-testid="wfswap-shift" data-id={s.id}
                  style={{ ...choiceStyle(on, T.p1), display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{dayLabel(s.date, "full")}</span>
                    <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 2, fontFamily: T.fontMono }}>
                      {s.start} – {s.end} <span style={{ fontFamily: T.font }}>· {s.apptCount} {s.apptCount === 1 ? t("workforce.swap.apptBooked", "appt booked") : t("workforce.swap.apptsBooked", "appts booked")}</span>
                    </span>
                  </span>
                  {on ? <span style={{ color: T.p1, display: "inline-flex" }}><Icon.check size={14} /></span> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <FieldLabel>{t("workforce.swap.howRoute", "How should we route it?")}</FieldLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <button onClick={() => setRoute("marketplace")} data-testid="wfswap-route" data-id="marketplace" style={choiceStyle(route === "marketplace", T.p1)}>
            <span style={{ display: "block" }}>{t("workforce.swap.postToMarketplace", "Post to marketplace")}</span>
            <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: T.soft, marginTop: 2 }}>{t("workforce.swap.anyoneQualified", "Anyone qualified can pick up")}</span>
          </button>
          <button onClick={() => setRoute("direct")} data-testid="wfswap-route" data-id="direct" style={choiceStyle(route === "direct", T.p1)}>
            <span style={{ display: "block" }}>{t("workforce.swap.directSwap", "Direct swap")}</span>
            <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: T.soft, marginTop: 2 }}>{t("workforce.swap.tradeWithOneColleague", "Trade with one colleague")}</span>
          </button>
        </div>
      </div>
    </Drawer>
  );
}
